import { useEffect, useReducer } from 'react';
import { treeTypes } from './types';
import { difference, focusRef, isBranchNode, propagateSelectChange, symmetricDifference, usePrevious } from './utils';

type actionType = {
	keepFocus: any;
	NotUserAction: any;
	NotUserActions: any;
	selects: any;
	select: any;
	ids: [];
	id: any;
	lastInteractedWith: any;
	type: any;
	multiSelect: any;
};

type stateType = {
	expandedIds: any;
	disabledIds: any;
	halfSelectedIds: Set<[]>;
	selectedIds: Set<[]>;
	tabbableId: any;
	lastUserSelect: any;
};

export const treeReducer: any = (state: stateType, action: actionType) => {
	switch (action.type) {
		case treeTypes.collapse: {
			const expandedIds = new Set(state.expandedIds);
			expandedIds.delete(action.id);
			return {
				...state,
				expandedIds,
				tabbableId: action.id,
				isFocused: true,
				lastAction: action.type,
				lastInteractedWith: action.lastInteractedWith,
			};
		}
		case treeTypes.collapseMany: {
			const expandedIds = new Set(state.expandedIds);
			for (const id of action.ids) {
				expandedIds.delete(id);
			}
			return {
				...state,
				expandedIds,
				lastAction: action.type,
				lastInteractedWith: action.lastInteractedWith,
			};
		}
		case treeTypes.expand: {
			const expandedIds = new Set(state.expandedIds);
			expandedIds.add(action.id);
			return {
				...state,
				expandedIds,
				tabbableId: action.id,
				isFocused: true,
				lastAction: action.type,
				lastInteractedWith: action.lastInteractedWith,
			};
		}
		case treeTypes.expandMany: {
			const expandedIds = new Set([...state.expandedIds, ...action.ids]);
			return {
				...state,
				expandedIds,
				lastAction: action.type,
				lastInteractedWith: action.lastInteractedWith,
			};
		}
		case treeTypes.toggle: {
			const expandedIds = new Set(state.expandedIds);
			if (state.expandedIds.has(action.id)) expandedIds.delete(action.id);
			else expandedIds.add(action.id);
			return {
				...state,
				expandedIds,
				tabbableId: action.id,
				isFocused: true,
				lastAction: action.type,
				lastInteractedWith: action.lastInteractedWith,
			};
		}
		case treeTypes.halfSelect: {
			if (state.disabledIds.has(action.id)) return state;
			const halfSelectedIds = new Set(state.halfSelectedIds);
			const selectedIds = new Set(state.selectedIds);
			halfSelectedIds.add(action.id);
			selectedIds.delete(action.id);
			return {
				...state,
				selectedIds,
				halfSelectedIds,
				lastAction: action.type,
				lastInteractedWith: action.lastInteractedWith,
			};
		}
		case treeTypes.select: {
			if (state.disabledIds.has(action.id)) return state;
			let selectedIds;
			if (action.multiSelect) {
				selectedIds = new Set(state.selectedIds);
				selectedIds.add(action.id);
			} else {
				selectedIds = new Set();
				selectedIds.add(action.id);
			}

			const halfSelectedIds = new Set(state.halfSelectedIds);
			halfSelectedIds.delete(action.id);
			return {
				...state,
				selectedIds,
				halfSelectedIds,
				tabbableId: action.keepFocus ? state.tabbableId : action.id,
				isFocused: true,
				lastUserSelect: action.NotUserAction ? state.lastUserSelect : action.id,
				lastAction: action.type,
				lastInteractedWith: action.lastInteractedWith,
			};
		}
		case treeTypes.deselect: {
			if (state.disabledIds.has(action.id)) return state;
			let selectedIds;
			if (action.multiSelect) {
				selectedIds = new Set(state.selectedIds);
				selectedIds.delete(action.id);
			} else {
				selectedIds = new Set();
			}
			const halfSelectedIds = new Set(state.halfSelectedIds);
			halfSelectedIds.delete(action.id);
			return {
				...state,
				selectedIds,
				halfSelectedIds,
				tabbableId: action.keepFocus ? state.tabbableId : action.id,
				isFocused: true,
				lastUserSelect: action.NotUserAction ? state.lastUserSelect : action.id,
				lastAction: action.type,
				lastInteractedWith: action.lastInteractedWith,
			};
		}
		case treeTypes.toggleSelect: {
			if (state.disabledIds.has(action.id)) return state;
			let selectedIds;
			const isSelected = state.selectedIds.has(action.id);
			if (action.multiSelect) {
				selectedIds = new Set(state.selectedIds);
				if (isSelected) selectedIds.delete(action.id);
				else selectedIds.add(action.id);
			} else {
				selectedIds = new Set();
				if (!isSelected) selectedIds.add(action.id);
			}

			const halfSelectedIds = new Set(state.halfSelectedIds);
			halfSelectedIds.delete(action.id);
			return {
				...state,
				selectedIds,
				halfSelectedIds,
				tabbableId: action.id,
				isFocused: true,
				lastUserSelect: action.NotUserAction ? state.lastUserSelect : action.id,
				lastAction: action.type,
				lastInteractedWith: action.lastInteractedWith,
			};
		}
		case treeTypes.changeSelectMany: {
			let selectedIds;
			const ids = action.ids.filter((id: any) => !state.disabledIds.has(id));
			if (action.multiSelect) {
				if (action.select) {
					const setIds = new Set(ids);
					selectedIds = new Set([...Array.from(state.selectedIds), ...Array.from(setIds)]) as Set<[]>;
				} else {
					selectedIds = difference(state.selectedIds, new Set(ids)) as Set<[]>;
				}
				const halfSelectedIds = difference(state.halfSelectedIds, selectedIds);
				return {
					...state,
					selectedIds,
					halfSelectedIds,
					lastAction: action.type,
					lastInteractedWith: action.lastInteractedWith,
				};
			}
			return state;
		}
		case treeTypes.exclusiveChangeSelectMany: {
			let selectedIds;
			const ids = action.ids && action.ids.filter((id: any) => !state.disabledIds.has(id));
			if (action.multiSelect) {
				if (action.select) {
					selectedIds = new Set(ids);
				} else {
					selectedIds = difference(state.selectedIds, new Set(ids)) as Set<[]>;
				}
				const halfSelectedIds = difference(state.halfSelectedIds, selectedIds);
				return {
					...state,
					selectedIds,
					halfSelectedIds,
					lastAction: action.type,
					lastInteractedWith: action.lastInteractedWith,
				};
			}
			return state;
		}
		case treeTypes.focus:
			return {
				...state,
				tabbableId: action.id,
				isFocused: true,
				lastAction: action.type,
				lastInteractedWith: action.lastInteractedWith,
			};
		case treeTypes.blur:
			return {
				...state,
				isFocused: false,
			};
		case treeTypes.disable: {
			const disabledIds = new Set(state.disabledIds);
			disabledIds.add(action.id);
			return {
				...state,
				disabledIds,
			};
		}
		case treeTypes.enable: {
			const disabledIds = new Set(state.disabledIds);
			disabledIds.delete(action.id);
			return {
				...state,
				disabledIds,
			};
		}
		default:
			//throw new Error("Invalid action passed to the reducer");
			return {};
	}
};

export const useTree = ({
	data,
	defaultExpandedIds,
	defaultSelectedIds,
	defaultDisabledIds,
	nodeRefs,
	onSelect,
	onExpand,
	multiSelect,
	propagateSelectUpwards,
}: any) => {
	const [state, dispatch]: any = useReducer(treeReducer, {
		selectedIds: new Set(defaultSelectedIds),
		tabbableId: data[0].children[0],
		isFocused: false,
		expandedIds: new Set(defaultExpandedIds),
		halfSelectedIds: new Set(),
		lastUserSelect: data[0].children[0],
		lastInteractedWith: null,
		disabledIds: new Set(defaultDisabledIds),
	});

	const {
		selectedIds,
		expandedIds,
		disabledIds,
		tabbableId,
		halfSelectedIds,
		lastAction,
		lastInteractedWith,
	}: any = state;
	const prevSelectedIds: Set<[]> = usePrevious(selectedIds) || new Set();
	const toggledIds: any = symmetricDifference(selectedIds, prevSelectedIds) || [];

	useEffect(() => {
		if (onSelect) {
			if (toggledIds) {
				for (const toggledId of toggledIds) {
					const isBranch = isBranchNode(data, toggledId);
					onSelect({
						element: data[toggledId],
						isBranch: isBranch,
						isExpanded: isBranch ? expandedIds.has(toggledId) : undefined,
						isSelected: selectedIds.has(toggledId),
						isDisabled: disabledIds.has(toggledId),
						isHalfSelected: isBranch ? halfSelectedIds.has(toggledId) : undefined,
						treeState: state,
					});
				}
			}
		}
	}, [data, selectedIds, expandedIds, disabledIds, halfSelectedIds, toggledIds, onSelect, state]);

	const prevExpandedIds: Set<[]> = usePrevious(expandedIds) || new Set();
	useEffect(() => {
		const toggledExpandIds: any = symmetricDifference(expandedIds, prevExpandedIds) || [];
		if (onExpand) {
			for (const id of toggledExpandIds) {
				onExpand({
					element: data[id],
					isExpanded: expandedIds.has(id),
					isSelected: selectedIds.has(id),
					isDisabled: disabledIds.has(id),
					isHalfSelected: halfSelectedIds.has(id),
					treeState: state,
				});
			}
		}
	}, [data, selectedIds, expandedIds, disabledIds, halfSelectedIds, prevExpandedIds, onExpand, state]);

	//Update parent if a child changes
	useEffect(() => {
		if (propagateSelectUpwards && multiSelect) {
			const idsToUpdate: Set<any> = new Set(toggledIds);
			if (lastInteractedWith) {
				idsToUpdate.add(lastInteractedWith);
			}
			const { every, some, none }: any = propagateSelectChange(data, idsToUpdate, selectedIds, disabledIds);
			for (const id of every) {
				if (!selectedIds.has(id)) {
					dispatch({
						type: treeTypes.select,
						id,
						multiSelect,
						keepFocus: true,
						NotUserAction: true,
						lastInteractedWith,
					});
				}
			}
			for (const id of some) {
				if (!halfSelectedIds.has(id))
					dispatch({
						type: treeTypes.halfSelect,
						id,
						multiSelect,
						keepFocus: true,
						NotUserAction: true,
						lastInteractedWith,
					});
			}
			for (const id of none) {
				if (selectedIds.has(id) || halfSelectedIds.has(id))
					dispatch({
						type: treeTypes.deselect,
						id,
						multiSelect,
						keepFocus: true,
						NotUserAction: true,
						lastInteractedWith,
					});
			}
		}
	}, [
		data,
		multiSelect,
		propagateSelectUpwards,
		selectedIds,
		expandedIds,
		disabledIds,
		halfSelectedIds,
		lastAction,
		prevSelectedIds,
		toggledIds,
		lastInteractedWith,
	]);

	//Focus
	useEffect(() => {
		if (lastInteractedWith == null) return;
		else if (tabbableId != null) {
			const tabbableNode = nodeRefs.current[tabbableId];
			focusRef(tabbableNode);
		}
	}, [tabbableId, nodeRefs, lastInteractedWith]);

	return [state, dispatch];
};
